import {
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useMemo,
} from "react";
import {
    findInvoiceInListCache,
    invalidateInvoiceQueries,
} from "@api/invoice.api";
import { Invoice } from "@common/types/invoice";
import { useScheduleLifecycleActions } from "@components/accounting/schedules/applications/useScheduleLifecycleActions";
import { useInlineScheduleLockedPeriods } from "@components/accounting/schedules/useInlineScheduleLockedPeriods";
import type { InvoiceDetailsScheduleCallbacks } from "./InvoiceDetailsSchedule";
import { useInvoiceScheduleApply } from "./useInvoiceScheduleApply";

/**
 * Schedule wiring for a surface that shows a selected invoice in the side
 * panel. The panel renders a snapshot, so every schedule mutation (direct
 * apply, specific-date confirm, lifecycle action, removal) refetches the
 * invoice lists and copies the fresh row back into the snapshot.
 */
export function useInvoiceDetailsScheduleCallbacks(
    setSelectedInvoice: Dispatch<SetStateAction<Invoice | undefined>>,
): {
    scheduleCallbacks: Required<InvoiceDetailsScheduleCallbacks>;
    scheduleOverlays: ReactNode;
} {
    const refreshSelectedInvoice = useCallback(async () => {
        await invalidateInvoiceQueries();
        setSelectedInvoice((previous) =>
            previous == null
                ? previous
                : (findInvoiceInListCache(previous.id) ?? previous),
        );
    }, [setSelectedInvoice]);

    const { showLockedPeriods, lockedPeriodsModal } =
        useInlineScheduleLockedPeriods();
    const { onRequestScheduleConfirm, onCreateSchedule, scheduleOverlays } =
        useInvoiceScheduleApply({
            onApplied: refreshSelectedInvoice,
            showLockedPeriods,
        });
    const { requestLifecycleAction, modals: lifecycleModals } =
        useScheduleLifecycleActions({
            onCompleted: () => void refreshSelectedInvoice(),
            showLockedPeriods,
        });

    const scheduleCallbacks = useMemo(
        () => ({
            onCreateSchedule,
            onRequestScheduleConfirm,
            onRequestLifecycleAction: requestLifecycleAction,
            onScheduleApplied: refreshSelectedInvoice,
            showLockedPeriods,
        }),
        [
            onCreateSchedule,
            onRequestScheduleConfirm,
            requestLifecycleAction,
            refreshSelectedInvoice,
            showLockedPeriods,
        ],
    );

    return {
        scheduleCallbacks,
        scheduleOverlays: (
            <>
                {scheduleOverlays}
                {lifecycleModals}
                {lockedPeriodsModal}
            </>
        ),
    };
}
